import dynamic from "next/dynamic";
import Link from "next/link";
import Service2 from "../../components/services/Service2";
import IndustryDetailSection from "../../components/reusable/IndustryDetailSection";
import industriesData from "../../utils/industries.json";
import Head from "next/head";
import ButtonArrow from "../../components/reusable/ButtonArrow";
import BottomNavigation from "../../components/reusable/BottomNavigation";
import SuccessStoryEmbed from "../../components/trust/SuccessStoryEmbed";
import ClientsStrip, { clients as allClients } from "../../components/services/ClientsStrip";
import MethodologyAnchor from "../../components/services/MethodologyAnchor";

const ContactUsHome = dynamic(() => import("../../components/homepage/ContactUsHome"), { ssr: false });
const Footer = dynamic(() => import("../../components/Footer"), { ssr: false });

export async function getStaticPaths() {
  const paths = industriesData.map(i => ({ params: { slug: i?.slug } }));
  return { paths, fallback: false };
}

export async function getStaticProps(context) {
  const slug = context.params.slug;
  const data = industriesData.filter(item => item.slug === slug)[0];
  return { props: { data, noAutoHeader: "no" } };
}

const IndustryPage = ({ data }) => {
  return (
    <div className="mx-auto max-w-[1600px]">
      <Head>
        <title>{data?.title}</title>
        <meta name="description" content={`${data?.metaDesc}`} />
        <meta name="keywords" content={`${data?.metaKeyword}`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`${data.mainHeading}`} />
        <meta property="og:description" content={`${data.metaDesc}`} />
        <meta property="og:image" content={`https://www.itmtb.com${data?.image}`} />
        <meta property="og:url" content={`https://www.itmtb.com/${data.link}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:description" content={`${data?.metaDesc}`} />
        <meta name="twitter:title" content={`${data.mainHeading}`} />
        <meta name="twitter:image" content={`https://www.itmtb.com${data?.image}`} />
        <link rel="icon" type="image/svg" href="/favicon.svg" sizes="16x16" />
        <link rel="canonical" href={`https://www.itmtb.com/${data.link}`} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              headline: data?.heading,
              description: data?.metaDesc,
              author: { "@type": "Organization", name: "ITMTB" },
              publisher: {
                "@type": "Organization",
                name: "ITMTB",
                logo: { "@type": "ImageObject", url: "https://www.itmtb.com/logo.png" }
              },
              image: `https://www.itmtb.com${data?.image}`,
              keywords: data?.metaKeyword?.split(",").map(k => k.trim()),
              mainEntityOfPage: {
                "@type": "WebPage",
                "@id": `https://www.itmtb.com/${data.link}`
              }
            })
          }}
        />
      </Head>

      <Service2
        mainHeading={data.mainHeading}
        heading={data.heading}
        paragraph={data.paragraph}
        bgPath={data.image}
        primaryCta={{ label: "Talk to us →", href: "/contact-us" }}
      />

      <SuccessStoryEmbed
        referTag={data.referTag}
        maxItems={3}
        heading={`Selected work in ${data.mainHeading.toLowerCase()}`}
      />

      {data.relatedStory ? (
        <section className="px-8 md:px-[60px] py-8 bg-white border-t border-gray-100">
          <p className="max-w-4xl text-sm text-gray-500 leading-relaxed">
            {data.relatedStory.prefix}{" "}
            <Link href={data.relatedStory.href}>
              <a className="text-indigo-600 font-medium hover:text-indigo-800 transition-colors">
                {data.relatedStory.label}
              </a>
            </Link>{" "}
            {data.relatedStory.suffix}
          </p>
        </section>
      ) : null}

      <ClientsStrip clients={allClients.filter(c => (data.clientNames || []).includes(c.name))} />

      <div className="bg-[#051747] py-[1px]">
        <div className="w-[90%] mx-auto mb-[150px]">
          <div className="text-slate-400">
            <h2 className="text-4xl mb-10 mt-[100px] text-white font-semibold">Landscape and Opportunities</h2>
            <ul className="text-xl grid gap-y-[22px]">
              {data.numbers.map((numer, idx) => (
                <li key={idx}>
                  <b className="text-3xl text-white"> {numer.number} </b> {numer.detail}
                </li>
              ))}
            </ul>
          </div>

          <hr className="my-20" />
          <IndustryDetailSection dataList={data.points} bg="bg-[#051747]" />
          <hr />

          {data.details2 ? (
            <div className="mb-[120px]">
              <h2 className="text-4xl mt-[60px] mb-[40px] font-semibold text-white">{data.details2.title}</h2>
              {data.details2.subtitle && <p className="text-slate-400 text-4xl">{data.details2.subtitle}</p>}
              <br />
              <div className="text-xl text-slate-400">{data.details2.desc}</div>
            </div>
          ) : null}

          <div>
            <h2 className="text-4xl mt-[60px] mb-[40px] font-semibold text-white">
              Why ITMTB for {data.mainHeading}?
            </h2>
            <div className="text-xl text-slate-400">{data.whyChooseUs}</div>
            <br />
            <ButtonArrow
              link="/contact-us"
              txt="Start a conversation"
              style="text-lg text-white [&>div]:border-b-[0px] hover:opacity-70"
            />
          </div>
        </div>
      </div>

      <MethodologyAnchor />
      <BottomNavigation />
      <div className="pt-5">
        <ContactUsHome />
      </div>
      <Footer />
    </div>
  );
};

export default IndustryPage;
